import { useCallback, useMemo } from 'react';
import { BulkSelectionHook, SelectAllState, Batch } from './types/types';

export const useBulkSelection = (
  availableBatches: Batch[],
  selectedBatchIds: number[],
  onSelectionChange: (selectedIds: number[]) => void
): BulkSelectionHook => {

  // Get only downloadable batches
  const downloadableBatches = useMemo(() =>
    availableBatches.filter(batch => batch.isDownloadable),
    [availableBatches]
  );

  const totalSelectableCount = downloadableBatches.length;
  const selectedBatchIdsSet = useMemo(() => new Set(selectedBatchIds), [selectedBatchIds]);

  const selectBatch = useCallback((batchId: number) => {
    const newSelected = new Set(selectedBatchIdsSet);
    if (newSelected.has(batchId)) {
      newSelected.delete(batchId);
    } else {
      newSelected.add(batchId);
    }
    onSelectionChange(Array.from(newSelected));
  }, [selectedBatchIdsSet, onSelectionChange]);

  const selectAll = useCallback(() => {
    if (selectedBatchIdsSet.size === totalSelectableCount && totalSelectableCount > 0) {
      // All selected, so deselect all
      onSelectionChange([]);
    } else {
      // Some or none selected, so select all downloadable
      const allDownloadableIds = downloadableBatches.map(batch => batch.id);
      onSelectionChange(allDownloadableIds);
    }
  }, [selectedBatchIdsSet.size, totalSelectableCount, downloadableBatches, onSelectionChange]);

  const clearSelection = useCallback(() => {
    onSelectionChange([]);
  }, [onSelectionChange]);

  const isSelected = useCallback((batchId: number) => selectedBatchIdsSet.has(batchId), [selectedBatchIdsSet]);

  const selectAllState: SelectAllState = useMemo(() => ({
    checked: selectedBatchIdsSet.size === totalSelectableCount && totalSelectableCount > 0,
    indeterminate: selectedBatchIdsSet.size > 0 && selectedBatchIdsSet.size < totalSelectableCount
  }), [selectedBatchIdsSet.size, totalSelectableCount]);

  return useMemo(() => ({
    selectedBatchIds,
    selectedCount: selectedBatchIds.length,
    selectBatch,
    selectAll,
    clearSelection,
    isSelected,
    selectAllState,
    canDownload: selectedBatchIds.length > 0
  }), [selectedBatchIds, selectBatch, selectAll, clearSelection, isSelected, selectAllState]);
};